<template>
  <div class="flex mb-12 launch-configuration-form">
    <div class="w-3/5 content-left-panel" id="launch-configuration-left-panel">
      <vs-alert
        v-if="!subscriptionProductExists"
        color="warning"
        icon-pack="feather"
        icon="icon-alert-triangle"
        class="mb-5"
      >
        No linked Subscriptions product; only planning fields are editable
      </vs-alert>

      <vs-alert
        v-if="failedSyncRetries.length"
        color="warning"
        icon-pack="feather"
        icon="icon-alert-triangle"
        class="mb-5 subscription-sync-warning"
      >
        <div class="flex items-center justify-between">
          <span>Saved locally — Subscriptions sync failed</span>
          <vs-button
            class="ml-3"
            type="border"
            color="warning"
            @click="retrySubscriptionSync"
          >
            Retry sync
          </vs-button>
        </div>
      </vs-alert>

      <div class="launch-configuration-fields">
        <div class="mt-6">
          <h4>General</h4>
        </div>
        <div class="flex flex-wrap -mx-2 mt-4">
          <div class="w-1/2 px-2 pb-6 launch-publish-date-field">
            <label class="vs-input--label">Publish date (local)</label>
            <vs-input
              class="w-full"
              type="datetime-local"
              v-model="form.publishStartsAt"
            />
          </div>
          <div
            v-if="showProductFields"
            class="w-full px-2 pb-6 launch-description-field"
          >
            <vs-textarea
              class="w-full"
              rows="4"
              label="Description"
              v-model="form.description"
              :disabled="!subscriptionProductExists"
            />
            <vs-textarea
              class="w-full mt-4"
              rows="4"
              label="Bundle description"
              v-model="form.bundleDescription"
              :disabled="!hasBundle || !subscriptionProductExists"
            />
            <div
              v-if="hasBundle && subscriptionProductExists"
              class="flex justify-end items-center mt-2 launch-bundle-description-actions"
            >
              <vx-tooltip text="Copy description">
                <feather-icon
                  icon="CopyIcon"
                  svgClasses="h-5 w-5"
                  class="mr-4 cursor-pointer"
                  :class="{ 'text-grey': !form.description.trim() }"
                  :style="{
                    pointerEvents: form.description.trim() ? 'auto' : 'none',
                  }"
                  @click="copyDescriptionToBundle"
                />
              </vx-tooltip>
              <vx-tooltip text="Clear text">
                <feather-icon
                  icon="XIcon"
                  svgClasses="h-5 w-5"
                  class="cursor-pointer"
                  @click="clearBundleDescription"
                />
              </vx-tooltip>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <h4>Pricing</h4>
        </div>
        <div class="flex flex-wrap -mx-2 mt-4">
          <div
            v-if="showProductFields"
            class="w-1/2 px-2 pb-6 launch-tier-field"
          >
            <label class="vs-input--label">Plan</label>
            <vs-input class="w-full" :value="tier" disabled />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-merchant-id-field">
            <label class="vs-input--label">Bundle merchant id</label>
            <vs-input class="w-full" v-model="form.bundleMerchantId" />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-cont-merchant-id-field">
            <label class="vs-input--label">Bundle continuity merchant id</label>
            <vs-input class="w-full" v-model="form.bundleContMerchantId" />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-annual-merchant-id-field">
            <label class="vs-input--label"
              >Bundle annual plan merchant id</label
            >
            <vs-input class="w-full" v-model="form.bundleAnnualMerchantId" />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-price-field">
            <label class="vs-input--label">Bundle price</label>
            <vs-input
              class="w-full"
              type="number"
              step="0.01"
              min="0"
              v-model="form.bundlePrice"
            />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-price-3m-field">
            <label class="vs-input--label">Bundle price (3 months)</label>
            <vs-input
              class="w-full"
              type="number"
              step="0.01"
              min="0"
              v-model="form.bundlePrice3m"
            />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-price-annual-field">
            <label class="vs-input--label">Bundle price (annual)</label>
            <vs-input
              class="w-full"
              type="number"
              step="0.01"
              min="0"
              v-model="form.bundlePriceAnnual"
            />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-bundle-discounted-price-field">
            <label class="vs-input--label">
              Bundle discounted price (informational)
            </label>
            <vs-input
              class="w-full"
              type="number"
              step="0.01"
              min="0"
              v-model="form.bundleDiscountedPrice"
            />
          </div>
          <div class="w-full px-2 pb-6 launch-pricing-informational-note">
            <small class="text-sm text-grey">{{ informationalNote }}</small>
          </div>
          <div
            v-if="showProductFields"
            class="w-full px-2 pb-6 launch-promo-info-title-field"
          >
            <label class="vs-input--label">Promo info title</label>
            <vs-input
              class="w-full"
              v-model="form.promoInfoTitle"
              :disabled="!subscriptionProductExists"
            />
          </div>
        </div>

        <div class="mt-6">
          <h4>Voucher</h4>
        </div>
        <div class="flex flex-wrap -mx-2 mt-4">
          <div class="w-1/2 px-2 pb-6 launch-voucher-merchant-id-field">
            <label class="vs-input--label">Voucher merchant id</label>
            <vs-input class="w-full" v-model="form.voucherMerchantId" />
          </div>
          <div class="w-1/2 px-2 pb-6 launch-voucher-price-field">
            <label class="vs-input--label">Standalone price</label>
            <vs-input
              class="w-full"
              type="number"
              step="0.01"
              min="0"
              v-model="form.voucherPrice"
            />
          </div>
          <div class="w-full px-2 pb-6 launch-voucher-discounted-price-field">
            <label class="vs-input--label">
              Voucher discounted price (informational)
            </label>
            <vs-input
              class="w-full"
              type="number"
              step="0.01"
              min="0"
              v-model="form.voucherDiscountedPrice"
            />
          </div>
          <div class="w-full px-2 pb-6 launch-voucher-informational-note">
            <small class="text-sm text-grey">{{ informationalNote }}</small>
          </div>
        </div>

        <div class="mt-6">
          <h4>Renewal disclaimer</h4>
        </div>
        <div class="flex flex-wrap -mx-2 mt-4">
          <div
            v-if="showProductFields"
            class="w-full px-2 pb-6 launch-promo-info-extra-field"
          >
            <label class="vs-input--label">Promo info extra</label>
            <vs-input
              class="w-full"
              v-model="form.promoInfoExtra"
              :disabled="!subscriptionProductExists"
            />
          </div>
          <div
            v-if="showProductFields"
            class="w-full px-2 pb-6 launch-cart-renewal-disclaimer-field"
          >
            <label class="vs-input--label">Cart renewal disclaimer</label>
            <vs-input
              class="w-full"
              v-model="form.cartRenewalDisclaimer"
              :disabled="!subscriptionProductExists"
            />
          </div>
        </div>

        <div class="mt-6">
          <h4>Pre-sale</h4>
        </div>
        <div class="flex items-center mt-4 px-2 launch-presale-enabled-field">
          <vs-switch v-model="form.preSaleEnabled" class="mr-3" />
          <label>Pre-sale info</label>
        </div>
        <div v-if="form.preSaleEnabled" class="flex flex-wrap -mx-2 mt-4">
          <div class="w-1/2 px-2 pb-6 launch-presale-start-date-field">
            <label class="vs-input--label">Start date (local)</label>
            <vs-input
              class="w-full"
              type="datetime-local"
              v-model="form.preSaleStartsAt"
            />
          </div>
          <div
            v-if="showProductFields"
            class="w-1/2 px-2 pb-6 launch-presale-discount-label-field"
          >
            <label class="vs-input--label">Pre-sale discount label</label>
            <vs-input
              class="w-full"
              v-model="form.presaleDiscountLabel"
              :disabled="!subscriptionProductExists"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="w-2/5 pl-10 content-right-panel">
      <div
        v-if="canManageLaunch && showProductFields"
        class="admin-controls-box mb-5"
      >
        <h4 class="mb-4">Sellability controls</h4>

        <div class="launch-sellability-section">
          <div class="flex items-center mb-3 launch-voucher-sellable-field">
            <vs-switch
              :value="voucherSellable"
              :disabled="!canEditSellability"
              @input="onVoucherSellableInput"
              class="mr-3"
            />
            <label>Voucher sellable</label>
          </div>
          <div class="flex items-center launch-bundle-sellable-field">
            <vs-switch
              :value="bundleSellable"
              :disabled="!canEditSellability || bundleIsSellable === null"
              @input="onBundleSellableInput"
              class="mr-3"
            />
            <label>Bundle plan sellable</label>
          </div>
        </div>

        <div class="flex mt-4 update-sellability-button">
          <vs-button
            class="w-full"
            type="border"
            color="primary"
            icon-pack="feather"
            :disabled="isSaving || !hasPendingSellabilityChanges"
            :loading="isSaving"
            @click="saveSellabilityOnly"
          >
            {{ isSaving ? 'Updating sellability...' : 'Update sellability' }}
          </vs-button>
        </div>
      </div>

      <div
        v-else-if="showProductFields"
        class="launch-sellability-section mb-5"
      >
        <div class="flex items-center mb-3 launch-voucher-sellable-field">
          <vs-switch :value="voucherSellable" disabled class="mr-3" />
          <label>Voucher sellable</label>
        </div>
        <div class="flex items-center launch-bundle-sellable-field">
          <vs-switch :value="bundleSellable" disabled class="mr-3" />
          <label>Bundle plan sellable</label>
        </div>
      </div>

      <div v-if="showProductFields" class="pb-6 launch-phase-field">
        <label class="vs-input--label">Phase</label>
        <v-select
          v-model="form.phase"
          :clearable="false"
          :options="phaseOptions"
          :reduce="(option) => option.value"
          :disabled="!canManageLaunch || !subscriptionProductExists"
        />
      </div>

      <div class="flex -mx-2 action-buttons">
        <vs-button
          class="w-full mx-2"
          color="primary"
          icon-pack="feather"
          :disabled="isSaving || !hasPendingPatchChanges"
          :loading="isSaving"
          @click="saveLaunchConfiguration"
        >
          {{
            isSaving
              ? 'Saving launch configuration...'
              : 'Save launch configuration'
          }}
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import dayjs from 'dayjs'
import isEqual from 'lodash.isequal'
import vSelect from 'vue-select'
import { CERTIFICATION_ROLES, ROLE_ADMIN } from '@/constants/roles'
import { LAUNCH_CONFIGURATION_PHASE_OPTIONS } from '@/constants/certifications'
import { formatCentsToDollars, parseDollarsToCents } from '@/utils/utils'

// Content-owned planning data. `transform` both converts the value that is
// sent and decides what counts as a change, so prices are compared as cents
// instead of as the string the user typed.
const VOUCHER_PAYLOAD_FIELDS = [
  { key: 'voucherMerchantId', apiKey: 'merchant_id' },
  {
    key: 'voucherPrice',
    apiKey: 'price_cents',
    transform: parseDollarsToCents,
  },
  {
    key: 'voucherDiscountedPrice',
    apiKey: 'discounted_price_cents',
    transform: parseDollarsToCents,
  },
]

const BUNDLE_PAYLOAD_FIELDS = [
  { key: 'bundleMerchantId', apiKey: 'merchant_id' },
  { key: 'bundleContMerchantId', apiKey: 'cont_merchant_id' },
  { key: 'bundleAnnualMerchantId', apiKey: 'annual_plan_merchant_id' },
  { key: 'bundlePrice', apiKey: 'price_cents', transform: parseDollarsToCents },
  {
    key: 'bundlePrice3m',
    apiKey: 'price_3m_cents',
    transform: parseDollarsToCents,
  },
  {
    key: 'bundlePriceAnnual',
    apiKey: 'price_annual_cents',
    transform: parseDollarsToCents,
  },
  {
    key: 'bundleDiscountedPrice',
    apiKey: 'discounted_price_cents',
    transform: parseDollarsToCents,
  },
]

const INFORMATIONAL_NOTE =
  'These merchant ids and prices are informational (stored in Content; not synced to Subscriptions or checkout).'

const buildEmptyForm = () => ({
  description: '',
  bundleDescription: '',
  phase: '',
  publishStartsAt: '',
  preSaleEnabled: false,
  preSaleStartsAt: '',
  promoInfoTitle: '',
  promoInfoExtra: '',
  cartRenewalDisclaimer: '',
  presaleDiscountLabel: '',
  voucherMerchantId: '',
  voucherPrice: '',
  voucherDiscountedPrice: '',
  bundleMerchantId: '',
  bundleContMerchantId: '',
  bundleAnnualMerchantId: '',
  bundlePrice: '',
  bundlePrice3m: '',
  bundlePriceAnnual: '',
  bundleDiscountedPrice: '',
})

export default {
  name: 'launch-configuration-form',
  components: {
    'v-select': vSelect,
  },
  props: {
    certificationVersion: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: buildEmptyForm(),
      originalSnapshot: null,
      originalTexts: {
        promo_info_title: '',
        promo_info_extra: '',
        cart_renewal_disclaimer: '',
        presale_discount_label: '',
      },
      tier: '',
      voucherSellable: false,
      bundleSellable: false,
      bundleIsSellable: null,
      sellabilityChanges: {},
      subscriptionProductExists: true,
      failedSyncRetries: [],
      isSaving: false,
      phaseOptions: LAUNCH_CONFIGURATION_PHASE_OPTIONS,
      informationalNote: INFORMATIONAL_NOTE,
    }
  },
  computed: {
    ...mapGetters('users', ['currentUserRoles']),
    showProductFields() {
      return (
        (this.currentUserRoles || []).includes(ROLE_ADMIN) ||
        this.subscriptionProductExists
      )
    },
    canManageLaunch() {
      const roles = this.currentUserRoles || []
      return CERTIFICATION_ROLES.some((role) => roles.includes(role))
    },
    canEditSellability() {
      return this.canManageLaunch && this.subscriptionProductExists
    },
    hasBundle() {
      return this.bundleIsSellable !== null
    },
    hasPendingPatchChanges() {
      if (!this.originalSnapshot) return false
      return Object.keys(this.buildPatchPayload()).length > 0
    },
    hasPendingSellabilityChanges() {
      return Object.keys(this.sellabilityChanges).length > 0
    },
  },
  async mounted() {
    await this.loadLaunchConfiguration()
  },
  beforeDestroy() {
    this._launchConfigRequestToken = (this._launchConfigRequestToken || 0) + 1
    this.$vs.loading.close('#launch-configuration-left-panel > .con-vs-loading')
  },
  methods: {
    ...mapActions('certification', [
      'getLaunchConfigurationAction',
      'updateLaunchConfigurationAction',
      'updateLaunchSellabilityAction',
    ]),
    async loadLaunchConfiguration() {
      if (!this.certificationVersion || !this.certificationVersion.id) return

      const requestToken = (this._launchConfigRequestToken =
        (this._launchConfigRequestToken || 0) + 1)
      this.$vs.loading({
        container: '#launch-configuration-left-panel',
        scale: 0.6,
      })

      try {
        const response = await this.getLaunchConfigurationAction({
          certificationVersionId: this.certificationVersion.id,
        })
        if (requestToken !== this._launchConfigRequestToken) return
        this.syncFormFromConfiguration(response)
      } catch (error) {
        this.showError(error)
      } finally {
        if (requestToken === this._launchConfigRequestToken) {
          this.$vs.loading.close(
            '#launch-configuration-left-panel > .con-vs-loading'
          )
        }
      }
    },
    toDatetimeLocalInput(value) {
      if (!value) return ''
      const parsed = dayjs(value)
      return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : ''
    },
    toIsoString(value) {
      if (!value) return null
      const parsed = dayjs(value)
      return parsed.isValid() ? parsed.toISOString() : null
    },
    syncFormFromConfiguration(config = {}) {
      const voucher = config.voucher || {}
      const bundle = config.bundle || {}
      const texts = config.texts || {}
      const defaultTexts = texts.default || {}
      const preSaleTexts = texts.pre_sale || {}
      const preSale = config.pre_sale || null
      const publicSale = config.public_sale || {}

      this.tier = config.tier || ''
      this.subscriptionProductExists =
        config.subscription_product_exists !== false
      this.bundleIsSellable =
        config.bundle_is_sellable === null ||
        config.bundle_is_sellable === undefined
          ? null
          : Boolean(config.bundle_is_sellable)
      this.voucherSellable = Boolean(config.is_sellable)
      this.bundleSellable = Boolean(this.bundleIsSellable)
      this.sellabilityChanges = {}

      this.originalTexts = {
        promo_info_title: defaultTexts.promo_info_title || '',
        promo_info_extra: defaultTexts.promo_info_extra || '',
        cart_renewal_disclaimer: defaultTexts.cart_renewal_disclaimer || '',
        presale_discount_label: preSaleTexts.presale_discount_label || '',
      }

      this.form = {
        bundleDescription: config.bundle_description || '',
        description: config.description || '',
        phase: config.phase || '',
        publishStartsAt: this.toDatetimeLocalInput(publicSale.starts_at),
        preSaleEnabled: Boolean(preSale),
        preSaleStartsAt: this.toDatetimeLocalInput(
          preSale && preSale.starts_at
        ),
        promoInfoTitle: this.originalTexts.promo_info_title,
        promoInfoExtra: this.originalTexts.promo_info_extra,
        cartRenewalDisclaimer: this.originalTexts.cart_renewal_disclaimer,
        presaleDiscountLabel: this.originalTexts.presale_discount_label,
        voucherMerchantId: voucher.merchant_id || '',
        voucherPrice: formatCentsToDollars(voucher.price_cents),
        voucherDiscountedPrice: formatCentsToDollars(
          voucher.discounted_price_cents
        ),
        bundleMerchantId: bundle.merchant_id || '',
        bundleContMerchantId: bundle.cont_merchant_id || '',
        bundleAnnualMerchantId: bundle.annual_plan_merchant_id || '',
        bundlePrice: formatCentsToDollars(bundle.price_cents),
        bundlePrice3m: formatCentsToDollars(bundle.price_3m_cents),
        bundlePriceAnnual: formatCentsToDollars(bundle.price_annual_cents),
        bundleDiscountedPrice: formatCentsToDollars(
          bundle.discounted_price_cents
        ),
      }

      this.originalSnapshot = { ...this.form }
    },
    copyDescriptionToBundle() {
      if (!this.form.description.trim() || !this.hasBundle) return
      this.form.bundleDescription = this.form.description
    },
    clearBundleDescription() {
      if (!this.hasBundle) return
      this.form.bundleDescription = ''
    },
    onVoucherSellableInput(value) {
      this.voucherSellable = value
      this.sellabilityChanges = { ...this.sellabilityChanges, voucher: value }
    },
    onBundleSellableInput(value) {
      this.bundleSellable = value
      this.sellabilityChanges = {
        ...this.sellabilityChanges,
        bundle_plan: value,
      }
    },
    hasPendingFieldChanges() {
      if (!this.originalSnapshot) return false
      return !isEqual(this.form, this.originalSnapshot)
    },
    isDirty() {
      return (
        this.hasPendingFieldChanges() ||
        Object.keys(this.sellabilityChanges).length > 0
      )
    },
    buildTextsPayload() {
      const texts = {}
      const defaultTexts = {}

      if (this.form.promoInfoTitle !== this.originalTexts.promo_info_title) {
        defaultTexts.promo_info_title = this.form.promoInfoTitle
      }
      if (this.form.promoInfoExtra !== this.originalTexts.promo_info_extra) {
        defaultTexts.promo_info_extra = this.form.promoInfoExtra
      }
      if (
        this.form.cartRenewalDisclaimer !==
        this.originalTexts.cart_renewal_disclaimer
      ) {
        defaultTexts.cart_renewal_disclaimer = this.form.cartRenewalDisclaimer
      }
      if (Object.keys(defaultTexts).length) {
        texts.default = defaultTexts
      }

      if (
        this.form.presaleDiscountLabel !==
        this.originalTexts.presale_discount_label
      ) {
        texts.pre_sale = {
          presale_discount_label: this.form.presaleDiscountLabel,
        }
      }

      return texts
    },
    hasPreSaleChanged() {
      if (this.form.preSaleEnabled !== this.originalSnapshot.preSaleEnabled) {
        return true
      }
      if (!this.form.preSaleEnabled) return false
      return this.form.preSaleStartsAt !== this.originalSnapshot.preSaleStartsAt
    },
    buildPatchPayload() {
      const payload = {}

      if (this.form.description !== this.originalSnapshot.description) {
        payload.description = this.form.description
      }
      if (
        this.form.bundleDescription !== this.originalSnapshot.bundleDescription
      ) {
        payload.bundle_description = this.form.bundleDescription
      }
      if (
        this.canManageLaunch &&
        this.form.phase !== this.originalSnapshot.phase
      ) {
        payload.phase = this.form.phase
      }

      const texts = this.buildTextsPayload()
      if (Object.keys(texts).length) {
        payload.texts = texts
      }

      const publishChanged =
        this.form.publishStartsAt !== this.originalSnapshot.publishStartsAt

      // pre_sale.ends_at is always mirrored from public_sale.starts_at, so a
      // publish-date change must also re-send pre_sale (when enabled) to keep
      // ends_at in sync — not only when a pre-sale field itself changed.
      if (
        this.hasPreSaleChanged() ||
        (this.form.preSaleEnabled && publishChanged)
      ) {
        payload.pre_sale = this.form.preSaleEnabled
          ? {
              starts_at: this.toIsoString(this.form.preSaleStartsAt),
              ends_at: this.toIsoString(this.form.publishStartsAt),
            }
          : null
      }

      if (publishChanged) {
        payload.public_sale = {
          starts_at: this.toIsoString(this.form.publishStartsAt),
        }
      }

      const voucherPayload = this.buildPartialPayload(VOUCHER_PAYLOAD_FIELDS)
      if (Object.keys(voucherPayload).length) {
        payload.voucher = voucherPayload
      }

      const bundlePayload = this.buildPartialPayload(BUNDLE_PAYLOAD_FIELDS)
      if (Object.keys(bundlePayload).length) {
        payload.bundle = bundlePayload
      }

      return payload
    },
    buildPartialPayload(fields) {
      return fields.reduce((payload, { key, apiKey, transform }) => {
        const current = transform ? transform(this.form[key]) : this.form[key]
        const original = transform
          ? transform(this.originalSnapshot[key])
          : this.originalSnapshot[key]

        if (current !== original) {
          payload[apiKey] = current
        }

        return payload
      }, {})
    },
    notifySyncFailure(retry, subscriptionSync) {
      this.failedSyncRetries.push(retry)
      this.$vs.notify({
        title: 'Saved locally — Subscriptions sync failed',
        text:
          (subscriptionSync && subscriptionSync.detail) ||
          'Retry to sync with Subscriptions.',
        color: 'warning',
      })
    },
    async savePatch(payload) {
      const response = await this.updateLaunchConfigurationAction({
        certificationVersionId: this.certificationVersion.id,
        payload,
      })
      this.syncFormFromConfiguration(response)

      if (
        response.subscription_sync &&
        response.subscription_sync.status === 'failed'
      ) {
        this.notifySyncFailure(
          { type: 'patch', payload },
          response.subscription_sync
        )
        return
      }

      this.$vs.notify({
        title: 'Success',
        text: 'Launch configuration saved',
        color: 'success',
      })
    },
    async saveSellability(payload) {
      const response = await this.updateLaunchSellabilityAction({
        certificationVersionId: this.certificationVersion.id,
        payload,
      })
      if (response.voucher !== null && response.voucher !== undefined) {
        this.voucherSellable = Boolean(response.voucher)
      }
      if (response.bundle_plan !== null && response.bundle_plan !== undefined) {
        this.bundleIsSellable = Boolean(response.bundle_plan)
        this.bundleSellable = Boolean(response.bundle_plan)
      }
      this.sellabilityChanges = {}

      if (
        response.subscription_sync &&
        response.subscription_sync.status === 'failed'
      ) {
        this.notifySyncFailure(
          { type: 'sellability', payload },
          response.subscription_sync
        )
        return
      }

      this.$vs.notify({
        title: 'Success',
        text: 'Sellability updated',
        color: 'success',
      })
    },
    async saveLaunchConfiguration() {
      const patchPayload = this.buildPatchPayload()
      const hasPatchChanges = Object.keys(patchPayload).length > 0

      if (!hasPatchChanges) return

      this.isSaving = true
      this.$vs.loading({
        container: '#launch-configuration-left-panel',
        scale: 0.6,
      })

      try {
        await this.savePatch(patchPayload)
      } catch (error) {
        this.showError(error)
      } finally {
        this.isSaving = false
        this.$vs.loading.close(
          '#launch-configuration-left-panel > .con-vs-loading'
        )
      }
    },
    async saveSellabilityOnly() {
      if (!this.canManageLaunch) return
      if (!this.hasPendingSellabilityChanges) return

      this.isSaving = true
      this.$vs.loading({
        container: '#launch-configuration-left-panel',
        scale: 0.6,
      })

      try {
        await this.saveSellability({ ...this.sellabilityChanges })
      } catch (error) {
        this.showError(error)
      } finally {
        this.isSaving = false
        this.$vs.loading.close(
          '#launch-configuration-left-panel > .con-vs-loading'
        )
      }
    },
    async retrySubscriptionSync() {
      const retries = [...this.failedSyncRetries]
      this.failedSyncRetries = []
      this.isSaving = true

      try {
        for (const retry of retries) {
          try {
            if (retry.type === 'patch') {
              await this.savePatch(retry.payload)
            } else {
              await this.saveSellability(retry.payload)
            }
          } catch (error) {
            this.showError(error)
          }
        }
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-sync-warning .vs-button-stub,
.subscription-sync-warning .vs-button {
  white-space: nowrap;
}

.admin-controls-box {
  border: 1px solid #dfe4ea;
  border-radius: 6px;
  padding: 1rem;
}

@media (max-width: 1200px) {
  .launch-configuration-form {
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
  }

  .launch-configuration-form .content-left-panel,
  .launch-configuration-form .content-right-panel {
    padding-left: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100%;
  }

  .launch-configuration-form .content-right-panel {
    margin-top: 0;
    margin-bottom: 2rem;
    order: -1;
  }
}
</style>
